import { resolveEnvScopedUrl } from '@provider-directory/functions/resolve-env-scoped-url';
import { Environment } from '@provider-directory/services/getEnvironmentVariables';
import type { SubdirectoryParentProvidersUrl } from '@provider-directory/types/runtime-config.contracts';

/** Relative path used when running locally; the config carries no LOCAL entry. */
export const LOCAL_PARENT_PROVIDERS_PATH = '/providers';

/**
 * Resolves the "go to the parent directory" CTA base URL for the current
 * deployment environment.
 *
 * Deliberately a **pure lookup** of the literal configured URL: it must never
 * derive a host from `subdomainRedirect.canonicalSlug` or any
 * `<slug>.pds.<env>` pattern. A directory can therefore point the CTA at an
 * arbitrary host (e.g. https://doctors.newclient.org/providers) with seed data
 * alone — deriving it would silently lock every client into Fabric's host shape.
 *
 * LOCAL is hardcoded and NOT configurable — it short-circuits before the lookup
 * so that adding a `default` to a directory's map can never send a local run
 * off-site. An engineer who wants to exercise another environment's URL changes
 * `NEXT_PUBLIC_APP_ENV` instead of editing config.
 */
export const resolveSubdirectoryEmptyRedirectUrl = (
  urls: SubdirectoryParentProvidersUrl,
  env: Environment,
): string => {
  if (env === Environment.LOCAL) {
    return LOCAL_PARENT_PROVIDERS_PATH;
  }
  // Unreachable for a valid config (the schema requires `production` plus either
  // `default` or all of development/qa/staging); stays on-site if it ever is.
  return resolveEnvScopedUrl(urls, env) ?? LOCAL_PARENT_PROVIDERS_PATH;
};
